<template>
  <main class="weekly-sales-page">
    <header class="page-heading">
      <div>
        <span class="eyebrow">CONTROL OPERATIVO</span>
        <h1>{{ salesTitle }}</h1>
        <p>Enter the supervisor totals and compare them with the seven daily reports in Modato.</p>
      </div>
      <div class="page-actions">
        <v-chip v-if="autoPopulated && !loading" size="small" color="primary" variant="tonal" prepend-icon="mdi-database-sync-outline">Auto from Modato</v-chip>
        <v-chip v-else-if="studioId && !loading && !canEdit" size="small" color="grey" variant="tonal">View Only</v-chip>
        <span v-if="canEdit" :class="['save-status', saveState]">{{ saveStatusText }}</span>
        <v-btn
          variant="outlined"
          prepend-icon="mdi-content-copy"
          :loading="screenshotting"
          :disabled="loading || !rows.length || editingGoals"
          @click="copyScreenshot"
        >
          Screenshot
        </v-btn>
        <v-btn
          variant="outlined"
          :color="editingGoals ? 'grey' : 'primary'"
          :prepend-icon="editingGoals ? 'mdi-close' : 'mdi-target'"
          :disabled="loading || !rows.length || savingGoals || !canEditGoals"
          @click="toggleGoalEditing"
        >
          {{ editingGoals ? 'Cancel Goals' : 'Edit Goals' }}
        </v-btn>
        <v-btn
          v-if="editingGoals"
          color="success"
          prepend-icon="mdi-content-save-outline"
          :loading="savingGoals"
          @click="saveGoals"
        >
          Save Goals
        </v-btn>
      </div>
    </header>

    <div ref="weeklyScreenshot" :class="['weekly-screenshot', { 'is-capturing': screenshotting }]">
      <div v-if="screenshotting" class="screenshot-heading">
        <strong>{{ salesTitle }}</strong>
        <span>{{ weekLabel }}</span>
      </div>
    <v-card class="sales-controls" elevation="0">
      <div class="studio-control">
        <span>Studio</span>
        <strong v-if="autoPopulated" class="satellite-studio-label">Satellite</strong>
        <v-select
          v-else
          v-model="studioId"
          :items="studios"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
        />
      </div>

      <div class="compare-control">
        <v-btn
          v-if="!autoPopulated"
          icon="mdi-compare-horizontal"
          size="small"
          :variant="showComparison ? 'tonal' : 'outlined'"
          :disabled="loading || !rows.length"
          title="Compare supervisor totals with Modato"
          aria-label="Compare supervisor totals with Modato"
          @click="showComparison = !showComparison"
        />
      </div>

      <div class="week-controls">
        <v-btn icon="mdi-chevron-left" variant="text" aria-label="Previous week" @click="moveWeek(-1)" />
        <div class="week-label">
          <small>SUNDAY — SATURDAY</small>
          <strong>{{ weekLabel }}</strong>
        </div>
        <v-btn icon="mdi-chevron-right" variant="text" aria-label="Next week" @click="moveWeek(1)" />
      </div>

      <div class="sales-controls-actions">
        <v-btn size="small" variant="text" @click="goToLatestCompletedWeek">Current Week</v-btn>
      </div>
    </v-card>

    <section class="summary-grid">
      <article class="summary-card summary-card--sales">
        <span>Total Sales</span><strong>${{ money(supervisorTotal) }}</strong><small>{{ autoPopulated ? 'Calculated automatically' : 'Entered for this week' }}</small>
      </article>
      <article class="summary-card summary-card--goal">
        <span>Weekly Goal</span><strong>${{ money(studioGoal) }}</strong><small>Daily model goals × 6</small>
      </article>
      <article class="summary-card summary-card--performance">
        <span>Studio Achievement</span><strong>{{ percent(studioAchievement) }}</strong><small>Supervisor sales versus goal</small>
      </article>
    </section>

    <section v-if="!loading && rows.length" class="sales-panel site-totals-panel">
      <div class="table-wrap">
        <table aria-label="Weekly site totals across all shifts">
          <colgroup>
            <col class="weekly-model-col">
            <col v-for="site in sites" :key="site.key" class="weekly-site-col">
            <col class="weekly-goal-col">
            <col class="weekly-total-col">
            <col class="weekly-achievement-col">
          </colgroup>
          <tbody>
            <tr class="site-totals-row">
              <td class="model-column"><strong>Site Totals</strong><small>All shifts</small></td>
              <td v-for="site in sites" :key="site.key" class="sales-input-cell">
                <small>{{ site.label }}</small><strong>${{ money(siteTotal(rows, site.key)) }}</strong>
              </td>
              <td colspan="3" class="site-totals-achievement">
                <small>Studio Achievement</small>
                <div :class="['achievement-bar', achievementClass(studioAchievement)]">
                  <div class="achievement-bar__fill" :style="achievementStyle(studioAchievement)"></div>
                  <strong>{{ percent(studioAchievement) }}</strong>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <v-alert v-if="error" type="warning" variant="tonal" closable class="mb-4" @click:close="error = ''">{{ error }}</v-alert>

    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" />
      <span>Loading weekly sales…</span>
    </div>

    <template v-else>
      <section v-for="group in shiftGroups" :key="group.key" class="sales-panel">
        <div :class="['shift-heading', `shift-heading--${group.key.toLowerCase()}`]">
          <div><strong>{{ group.label }}</strong><small>{{ group.rows.length }} models</small></div>
          <div class="shift-totals">
            <span>Goal <strong>${{ money(groupGoal(group.rows)) }}</strong></span>
            <span>Sales <strong>${{ money(groupTotal(group.rows)) }}</strong></span>
            <span>Achievement <strong>{{ percent(groupAchievement(group.rows)) }}</strong></span>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <colgroup>
              <col class="weekly-model-col">
              <col v-for="site in sites" :key="site.key" class="weekly-site-col">
              <col class="weekly-goal-col">
              <col class="weekly-total-col">
              <col class="weekly-achievement-col">
            </colgroup>
            <thead>
              <tr>
                <th class="model-column">Model</th>
                <th v-for="site in sites" :key="site.key">{{ site.label }}</th>
                <th>{{ editingGoals ? 'Daily Goal' : 'Weekly Goal' }}</th>
                <th>Total</th>
                <th>Goal %</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in group.rows" :key="row.model_id">
                <td class="model-cell">
                  <strong :title="row.fullName || ''">{{ row.stageName || row.fullName }}</strong>
                </td>
                <td v-for="site in sites" :key="site.key" class="sales-input-cell">
                  <span v-if="autoPopulated" class="automatic-sales-value">
                    {{ Number(row.entered[site.key]) > 0 ? `$${money(row.entered[site.key])}` : '—' }}
                  </span>
                  <v-text-field
                    v-else
                    v-model.number="row.entered[site.key]"
                    :class="{ 'sales-value-mismatch': isEnteredSiteMismatch(row, site.key) }"
                    :name="`weekly-sales-${weekStart}-${row.model_id}-${site.key}`"
                    autocomplete="off"
                    autocorrect="off"
                    density="compact"
                    hide-details
                    inputmode="decimal"
                    min="0"
                    :prefix="Number(row.entered[site.key]) > 0 ? '$' : ''"
                    :readonly="!canEdit"
                    spellcheck="false"
                    type="number"
                    variant="plain"
                    @update:model-value="queueAutoSave()"
                    @blur="handleSalesBlur(row, site.key)"
                  />
                  <span v-if="Number(row.entered[site.key]) > 0" class="capture-sales-value">${{ money(row.entered[site.key]) }}</span>
                  <small
                    v-if="showComparison && (Number(row.entered[site.key]) !== 0 || Number(row.modato[site.key]) !== 0)"
                    :class="['modato-helper', siteComparisonClass(row, site.key), { 'modato-helper--clickable': canEdit && !autoPopulated }]"
                    :title="canEdit && !autoPopulated ? 'Double-click to use this Modato value' : ''"
                    :tabindex="canEdit && !autoPopulated ? 0 : undefined"
                    @dblclick.prevent="acceptModatoValue(row, site.key)"
                    @keydown.enter.prevent="acceptModatoValue(row, site.key)"
                  >Modato ${{ money(row.modato[site.key]) }}</small>
                </td>
                <td class="money-cell goal-cell">
                  <v-text-field
                    v-if="editingGoals"
                    v-model.number="row.daily_goal"
                    :name="`weekly-goal-${weekStart}-${row.model_id}`"
                    autocomplete="off"
                    autocorrect="off"
                    density="compact"
                    hide-details
                    inputmode="decimal"
                    min="0"
                    prefix="$"
                    spellcheck="false"
                    type="number"
                    variant="plain"
                  />
                  <template v-else>${{ money(weeklyGoal(row)) }}</template>
                  <small v-if="editingGoals">Weekly ${{ money(weeklyGoal(row)) }}</small>
                </td>
                <td class="money-cell">${{ money(rowTotal(row)) }}</td>
                <td class="achievement-cell">
                  <div
                    :class="['achievement-bar', achievementClass(rowAchievement(row))]"
                    :aria-label="`Goal achievement ${percent(rowAchievement(row))}`"
                  >
                    <span class="achievement-bar__fill" :style="achievementStyle(rowAchievement(row))"></span>
                    <strong>{{ percent(rowAchievement(row)) }}</strong>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total {{ group.label }}</td>
                <td v-for="site in sites" :key="site.key">${{ money(siteTotal(group.rows, site.key)) }}</td>
                <td>${{ money(groupGoal(group.rows)) }}</td>
                <td>${{ money(groupTotal(group.rows)) }}</td>
                <td>{{ percent(groupAchievement(group.rows)) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div v-if="!rows.length" class="empty-state">
        <v-icon size="42">mdi-chart-box-outline</v-icon>
        <strong>No studio models for this week</strong>
      </div>
    </template>
    </div>

    <section v-if="showComparison && !autoPopulated && comparisonErrors.length" class="errors-panel">
      <div class="errors-heading">
        <div>
          <strong>Sales Differences</strong>
          <small>{{ comparisonErrors.length }} {{ comparisonErrors.length === 1 ? 'value needs' : 'values need' }} correction in Modato</small>
        </div>
        <v-btn
          size="small"
          variant="outlined"
          prepend-icon="mdi-content-copy"
          @click="copyComparisonErrors"
        >
          Copy Errors
        </v-btn>
      </div>
      <div class="table-wrap errors-table-wrap">
        <table class="errors-table">
          <thead>
            <tr>
              <th>Shift</th>
              <th>Model</th>
              <th>Site</th>
              <th>Modato</th>
              <th>Correct</th>
              <th>Difference</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="errorRow in comparisonErrors" :key="`${errorRow.modelId}-${errorRow.siteKey}`">
              <td>{{ errorRow.shift }}</td>
              <td class="error-model"><strong :title="errorRow.fullName || ''">{{ errorRow.stageName || errorRow.fullName }}</strong></td>
              <td>{{ errorRow.site }}</td>
              <td>${{ money(errorRow.modato) }}</td>
              <td class="error-correct">${{ money(errorRow.entered) }}</td>
              <td class="error-difference">{{ signedMoney(errorRow.difference) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2600">{{ snackbar.message }}</v-snackbar>
  </main>
</template>

<script>
import { addDays, addWeeks, format, parseISO, startOfWeek, subWeeks } from 'date-fns';
import secureAxios from '@/utils/secureAxios';

const SITES = [
  { key: 'flirt4free', label: 'Flirt4Free' },
  { key: 'streamate', label: 'Streamate' },
  { key: 'livejasmin', label: 'LiveJasmin' },
  { key: 'chaturbate', label: 'Chaturbate' },
  { key: 'stripchat', label: 'Stripchat' },
  { key: 'kwiky', label: 'Kwiky' },
];

export default {
  name: 'WeeklySales',
  data() {
    return {
      sites: SITES,
      studios: [],
      studioId: null,
      weekStart: '',
      rows: [],
      loading: false,
      saving: false,
      saveState: 'idle',
      autoSaveTimer: null,
      pendingAutoSave: null,
      editRevision: 0,
      savingGoals: false,
      editingGoals: false,
      goalSnapshot: {},
      screenshotting: false,
      showComparison: false,
      canEdit: false,
      canEditGoals: false,
      autoPopulated: false,
      error: '',
      snackbar: { show: false, message: '', color: 'success' },
      loadRequestId: 0,
    };
  },
  computed: {
    saveStatusText() {
      return {
        idle: 'Auto-save on',
        unsaved: 'Waiting to save…',
        saving: 'Saving…',
        saved: 'Saved',
        error: 'Save failed',
      }[this.saveState] || 'Auto-save on';
    },
    weekEnd() { return format(addDays(parseISO(this.weekStart), 6), 'yyyy-MM-dd'); },
    weekLabel() { return `${format(parseISO(this.weekStart), 'MMM d')} – ${format(parseISO(this.weekEnd), 'MMM d, yyyy')}`; },
    salesTitle() {
      if (this.autoPopulated) return 'Satellite Sales';
      const studioName = this.studios.find((studio) => Number(studio.id) === Number(this.studioId))?.name;
      if (!studioName) return 'Weekly Sales';
      const studioLabel = /^studio\b/i.test(studioName) ? studioName : `Studio ${studioName}`;
      return `${studioLabel} Sales`;
    },
    shiftGroups() {
      if (this.autoPopulated) {
        return [{ key: 'Satellite', label: 'Satellite', rows: this.rows }].filter((group) => group.rows.length);
      }
      return [
        { key: 'Morning', label: 'Morning', rows: this.rows.filter((row) => row.location === 'Morning') },
        { key: 'Evening', label: 'Evening', rows: this.rows.filter((row) => row.location === 'Evening') },
        { key: 'Night', label: 'Night', rows: this.rows.filter((row) => row.location === 'Night') },
      ].filter((group) => group.rows.length);
    },
    supervisorTotal() { return this.rows.reduce((sum, row) => sum + this.rowTotal(row), 0); },
    studioGoal() { return this.rows.reduce((sum, row) => sum + this.weeklyGoal(row), 0); },
    studioAchievement() { return this.studioGoal > 0 ? (this.supervisorTotal / this.studioGoal) * 100 : 0; },
    comparisonErrors() {
      if (!this.showComparison || this.autoPopulated) return [];
      return this.rows.flatMap((row) => this.sites
        .filter((site) => this.hasEnteredSiteValue(row, site.key)
          && !this.amountsMatch(row.entered?.[site.key], row.modato?.[site.key]))
        .map((site) => {
          const entered = this.toCents(row.entered?.[site.key]) / 100;
          const modato = this.toCents(row.modato?.[site.key]) / 100;
          return {
            modelId: row.model_id,
            fullName: row.fullName,
            stageName: row.stageName,
            shift: row.location,
            siteKey: site.key,
            site: site.label,
            entered,
            modato,
            difference: entered - modato,
          };
        }));
    },
  },
  watch: {
    weekStart() { if (this.weekStart) this.loadWeek(); },
    studioId() { if (this.weekStart && this.studioId) this.loadWeek(); },
  },
  async created() {
    await this.loadStudios();
    this.weekStart = this.latestCompletedWeekStart();
  },
  beforeUnmount() {
    clearTimeout(this.autoSaveTimer);
    if (this.pendingAutoSave) this.saveWeek(this.pendingAutoSave);
  },
  methods: {
    async loadStudios() {
      try {
        const { data } = await secureAxios.get('/api/model-schedule/studios');
        this.studios = data || [];
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const ownStudio = this.studios.find((studio) => Number(studio.id) === Number(user.studio_id));
        this.studioId = ownStudio?.id || this.studios[0]?.id || null;
      } catch (error) {
        this.error = 'Unable to load studios.';
      }
    },
    latestCompletedWeekStart() {
      const currentSunday = startOfWeek(new Date(), { weekStartsOn: 0 });
      return format(subWeeks(currentSunday, 1), 'yyyy-MM-dd');
    },
    moveWeek(direction) { this.weekStart = format(addWeeks(parseISO(this.weekStart), direction), 'yyyy-MM-dd'); },
    goToLatestCompletedWeek() { this.weekStart = this.latestCompletedWeekStart(); },
    async loadWeek() {
      const requestId = ++this.loadRequestId;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await secureAxios.get('/api/weekly-sales/week', {
          params: { week_start: this.weekStart, studio_id: this.studioId },
        });
        if (requestId !== this.loadRequestId) return;
        this.canEdit = data.can_edit === true;
        this.canEditGoals = data.can_edit_goals === true;
        this.autoPopulated = data.auto_populated === true;
        this.sites = Array.isArray(data.sites) && data.sites.length ? data.sites : SITES;
        this.saveState = 'idle';
        this.editingGoals = false;
        this.rows = (data.rows || []).map((row) => ({
          ...row,
          entered: Object.fromEntries(this.sites.map((site) => {
            const value = Number(row.entered?.[site.key] || 0);
            return [site.key, value > 0 ? value : ''];
          })),
          modato: Object.fromEntries(this.sites.map((site) => [site.key, Number(row.modato?.[site.key] || 0)])),
        }));
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.rows = [];
        this.error = error.response?.data?.message || 'Unable to load weekly sales.';
      } finally {
        if (requestId === this.loadRequestId) this.loading = false;
      }
    },
    autoSavePayload() {
      return {
        studioId: Number(this.studioId),
        weekStart: this.weekStart,
        revision: this.editRevision,
        entries: this.rows.map((row) => ({
          model_id: row.model_id,
          ...Object.fromEntries(this.sites.map((site) => [site.key, Number(row.entered?.[site.key] || 0)])),
        })),
      };
    },
    queueAutoSave(delay = 650) {
      if (!this.canEdit || this.loading || !this.rows.length) return;
      this.editRevision += 1;
      this.saveState = 'unsaved';
      this.error = '';
      this.pendingAutoSave = this.autoSavePayload();
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => {
        const payload = this.pendingAutoSave;
        this.pendingAutoSave = null;
        this.saveWeek(payload);
      }, delay);
    },
    async saveWeek(payload = this.autoSavePayload()) {
      if (!payload?.studioId || !payload?.weekStart) return;
      this.saving = true;
      if (payload.revision === this.editRevision) this.saveState = 'saving';
      try {
        await secureAxios.put('/api/weekly-sales/week', {
          studio_id: payload.studioId,
          week_start: payload.weekStart,
          entries: payload.entries,
        });
        if (payload.revision === this.editRevision) {
          this.rows.forEach((row) => { row.saved = true; });
          this.saveState = 'saved';
        }
      } catch (error) {
        if (payload.revision === this.editRevision) {
          this.saveState = 'error';
          this.error = error.response?.data?.message || 'Unable to auto-save weekly sales.';
        }
      } finally {
        this.saving = false;
      }
    },
    toggleGoalEditing() {
      if (!this.canEditGoals) return this.notify('This account can view goals but cannot edit them.', 'warning');
      if (this.editingGoals) {
        this.rows.forEach((row) => { row.daily_goal = Number(this.goalSnapshot[row.model_id] ?? row.daily_goal); });
        this.editingGoals = false;
        return;
      }
      this.goalSnapshot = Object.fromEntries(this.rows.map((row) => [row.model_id, Number(row.daily_goal || 0)]));
      this.editingGoals = true;
    },
    async saveGoals() {
      const invalid = this.rows.some((row) => !Number.isFinite(Number(row.daily_goal)) || Number(row.daily_goal) < 0);
      if (invalid) return this.notify('Every daily goal must be zero or a positive number.', 'error');
      this.savingGoals = true;
      try {
        const { data } = await secureAxios.put('/api/weekly-sales/goals', {
          studio_id: this.studioId,
          week_start: this.weekStart,
          goals: this.rows.map((row) => ({ model_id: row.model_id, daily_goal: Number(row.daily_goal) })),
        });
        this.rows.forEach((row) => { row.weekly_goal = this.weeklyGoal(row); });
        this.editingGoals = false;
        this.notify(data.message || 'Goals updated');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save goals.', 'error');
      } finally {
        this.savingGoals = false;
      }
    },
    async copyScreenshot() {
      if (!this.$refs.weeklyScreenshot || this.screenshotting) return;
      this.screenshotting = true;
      try {
        await this.$nextTick();
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(this.$refs.weeklyScreenshot, {
          backgroundColor: '#ffffff',
          scale: Math.min(2, window.devicePixelRatio || 1.5),
          useCORS: true,
          logging: false,
        });
        const image = await new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Unable to create image')), 'image/png'));
        if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') throw new Error('Image clipboard is not supported');
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': image })]);
        this.notify('Weekly Sales screenshot copied. Paste it into WhatsApp.');
      } catch (error) {
        console.error('Unable to copy Weekly Sales screenshot:', error);
        this.notify('Could not copy the screenshot. Please try again in Chrome.', 'error');
      } finally {
        this.screenshotting = false;
      }
    },
    rowTotal(row) { return this.sites.reduce((sum, site) => sum + Number(row.entered?.[site.key] || 0), 0); },
    handleSalesBlur(row, key) {
      if (Number(row.entered?.[key] || 0) <= 0) row.entered[key] = '';
      this.queueAutoSave(0);
    },
    acceptModatoValue(row, key) {
      if (!this.canEdit || this.autoPopulated) return;
      row.entered[key] = this.toCents(row.modato?.[key]) / 100;
      this.queueAutoSave(0);
    },
    async copyComparisonErrors() {
      if (!this.comparisonErrors.length) return;
      const shiftLabels = { Morning: 'Mañana', Evening: 'Tarde', Night: 'Noche', Satellite: 'Satélite' };
      const lines = [
        `${this.salesTitle} — ${this.weekLabel}`,
        'Por favor corregir estas diferencias en Modato:',
        ...this.comparisonErrors.map((item) => (
          `${shiftLabels[item.shift] || item.shift} — ${item.stageName || item.fullName} — ${item.site}: `
          + `Modato $${this.money(item.modato)} | Correcto $${this.money(item.entered)} | `
          + `Diferencia ${this.signedMoney(item.difference)}`
        )),
      ];
      try {
        await navigator.clipboard.writeText(lines.join('\n'));
        this.notify('Correction list copied.');
      } catch (error) {
        console.error('Unable to copy Weekly Sales differences:', error);
        this.notify('Could not copy the correction list.', 'error');
      }
    },
    weeklyGoal(row) { return Number(row.daily_goal || 0) * 6; },
    modatoTotal(row) { return this.sites.reduce((sum, site) => sum + Number(row.modato?.[site.key] || 0), 0); },
    rowDifference(row) { return this.rowTotal(row) - this.modatoTotal(row); },
    rowAchievement(row) { const goal = this.weeklyGoal(row); return goal > 0 ? (this.rowTotal(row) / goal) * 100 : 0; },
    siteTotal(rows, key) { return rows.reduce((sum, row) => sum + Number(row.entered?.[key] || 0), 0); },
    groupTotal(rows) { return rows.reduce((sum, row) => sum + this.rowTotal(row), 0); },
    groupGoal(rows) { return rows.reduce((sum, row) => sum + this.weeklyGoal(row), 0); },
    groupModatoTotal(rows) { return rows.reduce((sum, row) => sum + this.modatoTotal(row), 0); },
    groupDifference(rows) { return this.groupTotal(rows) - this.groupModatoTotal(rows); },
    groupAchievement(rows) { const goal = this.groupGoal(rows); return goal > 0 ? (this.groupTotal(rows) / goal) * 100 : 0; },
    money(value) { return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0)); },
    signedMoney(value) { const amount = Number(value || 0); return `${amount > 0 ? '+' : amount < 0 ? '−' : ''}$${this.money(Math.abs(amount))}`; },
    percent(value) { return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(Number(value || 0))}%`; },
    toCents(value) { return Math.round(Number(value || 0) * 100); },
    amountsMatch(first, second) { return this.toCents(first) === this.toCents(second); },
    hasEnteredSiteValue(row, key) {
      const value = row.entered?.[key];
      return value !== '' && value !== null && value !== undefined;
    },
    isEnteredSiteMismatch(row, key) {
      return this.showComparison
        && this.hasEnteredSiteValue(row, key)
        && !this.amountsMatch(row.entered?.[key], row.modato?.[key]);
    },
    isRowMatched(row) {
      return this.sites.every((site) => this.amountsMatch(row.entered?.[site.key], row.modato?.[site.key]));
    },
    achievementClass(value) {
      const achievement = Number(value || 0);
      if (achievement <= 0) return 'is-neutral';
      if (achievement <= 33) return 'is-low';
      if (achievement <= 66) return 'is-close';
      return achievement >= 100 ? 'is-good' : 'is-progress';
    },
    achievementStyle(value) {
      return { width: `${Math.min(100, Math.max(0, Number(value || 0)))}%` };
    },
    siteComparisonClass(row, key) {
      const entered = Number(row.entered?.[key] || 0);
      const modato = Number(row.modato?.[key] || 0);
      if (entered === 0 && modato === 0) return 'is-comparison-empty';
      if (!this.hasEnteredSiteValue(row, key)) return 'is-comparison-pending';
      return this.amountsMatch(entered, modato) ? 'is-comparison-matched' : 'is-comparison-different';
    },
    notify(message, color = 'success') { this.snackbar = { show: true, message, color }; },
  },
};
</script>

<style scoped>
.weekly-sales-page{max-width:1640px;margin:0 auto;padding:22px 22px 70px;color:#203247}.page-heading,.page-actions,.week-controls,.shift-heading,.shift-totals{display:flex;align-items:center}.page-heading,.shift-heading{justify-content:space-between}.page-heading{gap:20px;align-items:flex-end;margin:4px 2px 18px}.page-actions{justify-content:flex-end;gap:9px;flex-wrap:wrap}.eyebrow{font-size:11px;font-weight:800;letter-spacing:.14em;color:#2f7db9}.page-heading h1{margin:3px 0 4px;font-size:28px;line-height:1;letter-spacing:-.6px}.page-heading p{margin:0;color:#64768a;font-size:13px}.week-controls{gap:6px}.week-label{min-width:300px;text-align:center}.week-label small,.model-cell small{display:block;color:#718095;font-size:10px}.week-label strong{display:block;font-size:25px;font-weight:850;line-height:1.2}.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin:0 0 16px}.summary-card,.sales-panel{border:1px solid #dce4ec;border-radius:8px;background:#fff;box-shadow:0 1px 3px #1a2b3b16}.summary-card{padding:15px 18px;border-top:3px solid #4d91c9}.summary-card--goal{border-top-color:#d5a52d}.summary-card--performance{border-top-color:#27845b}.summary-card--comparison{border-top-color:#7652a4}.summary-card span,.summary-card small{display:block}.summary-card span{color:#65798c;font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase}.summary-card strong{display:block;margin:3px 0;font-size:23px}.summary-card small{color:#7b8996}.sales-controls{display:grid;grid-template-columns:165px 42px minmax(360px,1fr) auto;align-items:center;gap:10px;padding:11px 13px;margin-bottom:16px;border:1px solid #d8d8d8;border-radius:6px}.studio-control{display:grid;grid-template-columns:45px 1fr;align-items:center;gap:6px;color:#555;font-size:11px;font-weight:800;letter-spacing:.7px;text-transform:uppercase}.compare-control{display:flex;align-items:center;justify-content:center}.compare-control :deep(.v-btn){width:40px;height:40px;border-color:#b9c1c9;border-radius:4px}.week-controls{justify-content:center;min-width:0}.sales-controls-actions{display:flex;align-items:center;justify-content:flex-end}.sales-panel{margin-bottom:15px;overflow:hidden}.shift-heading{padding:11px 15px;background:linear-gradient(180deg,#17342f,#293e39);color:#fff}.shift-heading>div:first-child{display:flex;gap:10px;align-items:baseline}.shift-heading small{color:#c5d4cf}.shift-totals{gap:22px;font-size:12px}.shift-totals strong{margin-left:4px}.table-wrap{overflow-x:auto}table{width:100%;min-width:1420px;border-collapse:collapse;table-layout:fixed}th{padding:9px 7px;background:#fff;color:#263746;border-bottom:1px solid #cfd9e3;border-right:1px solid #edf1f4;font-size:9px;font-weight:800;text-align:center;text-transform:uppercase}td{height:54px;padding:4px 6px;border-bottom:1px solid #e1e7ed;border-right:1px solid #edf1f4;text-align:center;font-size:12px}tbody tr:nth-child(odd){background:#fff}tbody tr:nth-child(even){background:#f0f4f8}.model-column{width:205px;text-align:left}.model-cell{text-align:left}.model-cell strong{display:block;color:#203247}.sales-input-cell{width:118px}.sales-input-cell :deep(input){text-align:right;font-size:12px}.sales-input-cell small{display:block;margin-top:1px;color:#83909c;font-size:8px;white-space:nowrap}.money-cell{font-weight:700;white-space:nowrap}.achievement-cell{width:130px}.achievement-bar{position:relative;height:27px;overflow:hidden;border:1px solid #d7dee5;border-radius:4px;background:#eef1f4;color:#566574}.achievement-bar__fill{position:absolute;inset:0 auto 0 0;width:0;background:#eef1f4;transition:width .2s ease}.achievement-bar strong{position:relative;z-index:1;display:flex;height:100%;align-items:center;justify-content:center;font-size:11px;font-weight:850}.achievement-bar.is-low .achievement-bar__fill{background:#e77b7b}.achievement-bar.is-close .achievement-bar__fill{background:#e7c85c}.achievement-bar.is-good .achievement-bar__fill{background:#70bb82}.achievement-bar.is-low,.achievement-bar.is-close,.achievement-bar.is-good{color:#263746}.difference{display:inline-block;padding:4px 7px;border-radius:12px;font-weight:800;white-space:nowrap}.is-neutral{background:#eef1f4;color:#77838e}.is-matched{background:#e4f4e9;color:#247242}.is-negative{background:#fde8e8;color:#ad3030}.is-positive{background:#e7f0fb;color:#245f99}tfoot td{height:auto;padding:9px 6px;background:#f5f5f5;color:#203247;border-top:2px solid #ccd6df;border-bottom:0;font-weight:800}.loading-state,.empty-state{display:flex;min-height:280px;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#64768a}@media(max-width:1050px){.page-heading{align-items:flex-start;flex-direction:column;gap:15px}.summary-grid{grid-template-columns:repeat(2,1fr)}.sales-controls{grid-template-columns:1fr}.week-controls{justify-content:flex-start}.sales-controls-actions{justify-content:flex-start}}@media(max-width:650px){.weekly-sales-page{padding:18px 10px 55px}.week-controls{width:100%;flex-wrap:wrap}.week-label{min-width:220px}.summary-grid{grid-template-columns:1fr}}
.summary-grid{grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.summary-card--site-mix{display:flex;min-height:96px;align-items:center;justify-content:space-between;gap:8px;border-top-color:#8d69b0;background:linear-gradient(135deg,#fff 45%,#f7f2fb)}.site-mix-copy{min-width:0;flex:1}.site-mix-ranking{display:grid;grid-template-columns:repeat(3,max-content);gap:6px 9px;margin-top:8px}.site-mix-rank{display:flex!important;align-items:center;gap:3px;color:#34495d!important;font-size:9px!important}.site-mix-rank b{font-size:9px}.site-mix-rank i{width:6px;height:6px;border-radius:50%}.site-dot--flirt4free{background:#02a8e0}.site-dot--streamate{background:#23659f}.site-dot--livejasmin{background:#bc0002}.site-dot--chaturbate{background:#ffcb4a}.site-dot--stripchat{background:#008000}.site-dot--kwiky{background:#8e44ad}.site-mix-chart{position:relative;flex:0 0 82px;width:82px}.weekly-model-col{width:205px}.weekly-site-col{width:118px}.weekly-goal-col{width:188px}.weekly-total-col{width:189px}.weekly-achievement-col{width:130px}.site-totals-panel{margin-bottom:15px;border-top:3px solid #416f91}.site-totals-panel .table-wrap{overflow-x:hidden}.site-totals-row{background:linear-gradient(180deg,#f7fafc,#edf3f7)!important}.site-totals-row td{height:52px;border-bottom:0}.site-totals-row td:first-child{padding-left:15px}.site-totals-row small{display:block;margin-bottom:3px;color:#718095;font-size:8px;font-weight:800;text-transform:uppercase}.site-totals-row strong{color:#203247;font-size:12px}.site-totals-spacer{background:#f2f6f8}.satellite-studio-label{display:flex;height:40px;align-items:center;padding:0 10px;border:1px solid #c8d1da;border-radius:4px;color:#244d70;font-size:13px}.automatic-sales-value{font-weight:750;color:#244d70}.goal-cell small{display:block;color:#718095;font-size:9px}.modato-helper{font-size:10px!important;font-weight:750!important}.modato-helper.is-comparison-matched{color:#25814a}.is-comparison-different,.sales-value-mismatch :deep(input),.sales-value-mismatch :deep(.v-field__prefix){color:#c62828!important}.sales-value-mismatch :deep(input){font-weight:800}.modato-helper.is-comparison-pending{color:#426f8f}.modato-helper.is-comparison-empty{color:#8a97a3;font-weight:500!important}.capture-sales-value,.screenshot-heading{display:none}.weekly-screenshot.is-capturing{width:1400px;padding:12px;background:#fff}.weekly-screenshot.is-capturing .screenshot-heading{display:flex;align-items:baseline;justify-content:center;gap:15px;margin:2px 0 15px;color:#173f68;text-transform:uppercase}.weekly-screenshot.is-capturing .screenshot-heading strong{font-size:27px}.weekly-screenshot.is-capturing .screenshot-heading span{font-size:18px;font-weight:750}.weekly-screenshot.is-capturing .summary-grid{grid-template-columns:repeat(4,1fr);margin-top:0}.weekly-screenshot.is-capturing .summary-card--comparison,.weekly-screenshot.is-capturing .sales-controls,.weekly-screenshot.is-capturing .internal-column,.weekly-screenshot.is-capturing .modato-helper,.weekly-screenshot.is-capturing .sales-input-cell .v-input{display:none!important}.weekly-screenshot.is-capturing .capture-sales-value{display:inline;font-weight:700}.weekly-screenshot.is-capturing .table-wrap,.weekly-screenshot.is-capturing .sales-panel{overflow:visible}.weekly-screenshot.is-capturing table{min-width:0}.weekly-screenshot.is-capturing td{height:42px}
.site-mix-copy{align-self:flex-start}.site-mix-ranking{gap:8px 12px;margin-top:10px}.site-mix-rank{gap:4px;font-size:11px!important}.site-mix-rank b{font-size:11px}.site-mix-rank i{width:7px;height:7px}.site-mix-rank .site-dot--flirt4free{background:rgba(2,168,224,.5)}.site-mix-rank .site-dot--streamate{background:rgba(35,101,159,.5)}.site-mix-rank .site-dot--livejasmin{background:rgba(188,0,2,.5)}.site-mix-rank .site-dot--chaturbate{background:rgba(255,203,74,.8)}.site-mix-rank .site-dot--stripchat{background:rgba(0,128,0,.5)}.site-mix-rank .site-dot--kwiky{background:rgba(142,68,173,.5)}.modato-helper--clickable{cursor:pointer;text-decoration:underline;text-decoration-style:dotted;text-underline-offset:2px}.errors-panel{margin-top:18px;overflow:hidden;border:1px solid #e2b9b9;border-radius:8px;background:#fff;box-shadow:0 1px 3px #1a2b3b16}.errors-heading{display:flex;align-items:center;justify-content:space-between;gap:15px;padding:11px 14px;background:#fff5f5;border-bottom:1px solid #efd1d1}.errors-heading>div{display:flex;align-items:baseline;gap:10px}.errors-heading strong{color:#8f2727}.errors-heading small{color:#7f6161}.errors-table{min-width:760px;table-layout:auto}.errors-table th{background:#fffafa}.errors-table td{height:auto;padding:8px 10px}.error-model{text-align:left}.error-model strong,.error-model small{display:block}.error-model small{color:#718095;font-size:10px}.error-correct{font-weight:800}.error-difference{color:#c62828;font-weight:850}.save-status{font-size:12px;font-weight:700;color:#7b8792;white-space:nowrap}.save-status.unsaved,.save-status.saving{color:#a36d19}.save-status.saved{color:#52835b}.save-status.error{color:#c62828}
.achievement-bar.is-progress .achievement-bar__fill{background:#a8d7b2}.achievement-bar.is-good .achievement-bar__fill{background:#4ca667}.achievement-bar.is-progress,.achievement-bar.is-good{color:#263746}

/* Compact, spreadsheet-style weekly sales grid. */
.shift-heading{padding:8px 13px;background:#4177a8}
.shift-heading--morning{background:#4177a8}
.shift-heading--evening{background:#516c8a}
.shift-heading--night{background:#2c4e77}
.shift-heading small{color:rgba(255,255,255,.78)}
table{min-width:1160px}
th{padding:7px 5px;font-size:10px}
td{height:34px;padding:1px 5px;font-size:13px;font-variant-numeric:tabular-nums}
.weekly-model-col,.model-column{width:165px}
.weekly-site-col,.sales-input-cell{width:105px}
.weekly-goal-col{width:120px}
.weekly-total-col{width:120px}
.weekly-achievement-col,.achievement-cell{width:115px}
.model-cell strong{overflow:hidden;font-size:13px;font-weight:850;text-overflow:ellipsis;white-space:nowrap;cursor:help}
.sales-input-cell :deep(.v-field__input){min-height:28px;padding-top:0;padding-bottom:0}
.sales-input-cell :deep(input){font-size:15px;font-weight:800;line-height:1.1}
.sales-input-cell :deep(.v-field__prefix){font-size:14px;font-weight:800}
thead .model-column{text-align:center}
.automatic-sales-value,.money-cell{font-size:14px;font-weight:800}
.site-totals-row td{height:42px}
.site-totals-row strong{font-size:14px;font-weight:850}
.achievement-bar{height:25px}
.achievement-bar strong{font-size:12px}
tfoot td{height:38px;padding:6px 5px;font-size:13px;font-weight:850}
.weekly-screenshot.is-capturing table{min-width:0}
.summary-grid{grid-template-columns:repeat(3,minmax(0,1fr))}
.weekly-screenshot.is-capturing .summary-grid{grid-template-columns:repeat(3,minmax(0,1fr))}
.site-totals-panel{border-color:#dce4ec;border-top:3px solid #3f668e;background:#fff}
.site-totals-row{background:transparent!important}
.site-totals-row td{height:48px;border-right-color:rgba(54,90,125,.14);color:#172533}
.site-totals-row small,.site-totals-row strong{color:#172533;text-shadow:none}
.site-totals-row small{font-size:10px;font-weight:850;letter-spacing:.035em;opacity:1}
.site-totals-row strong{font-size:16px;font-weight:900}
.site-totals-row td:first-child strong{font-size:15px}
.site-totals-achievement{padding:6px 16px}
.site-totals-achievement>small{margin-bottom:4px;text-align:left}
.site-totals-achievement .achievement-bar{height:25px;background:#f2f5f7}
.site-totals-achievement .achievement-bar strong{font-size:12px;font-weight:850;text-shadow:none}
.shift-heading{display:grid;grid-template-columns:795fr 120fr 120fr 115fr;align-items:center}
.shift-totals{display:contents}
.shift-totals span{text-align:center;white-space:nowrap}
.shift-totals strong{display:inline;margin-left:4px}
@media(max-width:1050px){.summary-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:650px){.summary-grid{grid-template-columns:1fr}}
</style>
